import { useEffect, useRef, useState, useCallback } from "react";
import { ScanLine, X, CameraOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Tombol + modal pemindai barcode/IMEI lewat kamera.
 *
 * DUA MESIN PEMBACA, SATU ALUR KAMERA
 * -----------------------------------
 * 1. BarcodeDetector bawaan browser — cepat, nol unduhan. HANYA ada di
 *    Chrome/Edge berbasis Chromium (Android & desktop).
 * 2. ZXing (JavaScript) — cadangan untuk browser tanpa API itu.
 *
 * KENAPA CADANGAN ITU WAJIB: Apple mewajibkan SEMUA browser di iOS memakai
 * mesin WebKit, dan WebKit tidak punya BarcodeDetector. Jadi bukan cuma
 * Safari — Chrome dan Firefox di iPhone pun gagal. Tanpa mesin kedua,
 * seluruh pengguna iPhone tidak bisa memindai sama sekali.
 *
 * ZXing sengaja dimuat DINAMIS (import di dalam fungsi): pustakanya besar dan
 * hanya perlu diunduh oleh perangkat yang benar-benar memakainya. Pengguna
 * Android tidak ikut menanggung ongkosnya.
 *
 * Kedua jalur memakai daftar format yang SAMA supaya hasil pindai tidak
 * berbeda-beda tergantung merek HP karyawan.
 *
 * Catatan: kamera hanya aktif di secure context (HTTPS atau localhost).
 */

// Nama format versi BarcodeDetector (huruf kecil, sesuai spesifikasi WICG).
const FORMAT_NATIF = ["qr_code", "code_128", "code_39", "ean_13", "ean_8", "upc_a", "upc_e", "itf", "codabar"];

// Jeda antar upaya baca. 10x per detik sudah jauh lebih cepat dari gerakan
// tangan mengarahkan kamera, sekaligus menghemat baterai dan panas HP
// dibanding membaca tiap frame (60x per detik).
const JEDA_PINDAI_MS = 100;

// Frame dikecilkan sebelum dibaca ZXing. Barcode 1D dibaca per garis
// horizontal, jadi resolusi tinggi tidak menambah akurasi — hanya memperlambat.
const LEBAR_MAKS = 640;

// Dimuat sekali lalu dipakai ulang: modal yang dibuka-tutup berkali-kali
// tidak boleh mengunduh dan menyiapkan pustaka yang sama berulang kali.
let _zxingSiap = null;

function muatZxing() {
  if (!_zxingSiap) {
    _zxingSiap = import("@zxing/library").then((z) => {
      const hints = new Map();
      hints.set(z.DecodeHintType.POSSIBLE_FORMATS, [
        z.BarcodeFormat.QR_CODE,
        z.BarcodeFormat.CODE_128, z.BarcodeFormat.CODE_39,
        z.BarcodeFormat.EAN_13, z.BarcodeFormat.EAN_8,
        z.BarcodeFormat.UPC_A, z.BarcodeFormat.UPC_E,
        z.BarcodeFormat.ITF, z.BarcodeFormat.CODABAR,
      ]);
      // TRY_HARDER: barcode di stiker dus sering miring, buram, atau kena
      // pantulan lampu toko. Tanpa ini banyak frame gagal dibaca padahal
      // barcode-nya sudah pas di layar.
      hints.set(z.DecodeHintType.TRY_HARDER, true);
      const reader = new z.MultiFormatReader();
      reader.setHints(hints);
      return { z, reader };
    }).catch((e) => {
      // Gagal unduh chunk (jaringan putus) — jangan simpan promise gagal,
      // supaya percobaan berikutnya mengunduh ulang, bukan gagal selamanya.
      _zxingSiap = null;
      throw e;
    });
  }
  return _zxingSiap;
}

export default function BarcodeScanner({ onDetected, testId = "btn-scan-barcode", title = "Scan barcode / IMEI" }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const stop = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const close = useCallback(() => { stop(); setOpen(false); setStatus(""); }, [stop]);

  const start = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Kamera tidak tersedia. Halaman harus dibuka lewat HTTPS.");
      return;
    }
    setOpen(true);
    setStatus("Menyalakan kamera…");
  };

  useEffect(() => {
    if (!open) return;
    let batal = false;

    const bacaNatif = async (detector) => {
      const hasil = await detector.detect(videoRef.current);
      return hasil?.length ? (hasil[0].rawValue || "").trim() : "";
    };

    const bacaZxing = ({ z, reader }) => {
      const video = videoRef.current;
      const kanvas = canvasRef.current;
      if (!video || !kanvas || !video.videoWidth) return "";

      const skala = Math.min(1, LEBAR_MAKS / video.videoWidth);
      const w = Math.round(video.videoWidth * skala);
      const h = Math.round(video.videoHeight * skala);
      if (kanvas.width !== w) { kanvas.width = w; kanvas.height = h; }

      const ctx = kanvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, w, h);
      const rgba = ctx.getImageData(0, 0, w, h).data;

      // ZXing menerima satu byte kecerahan per piksel. Rumus (r + 2g + b) / 4
      // sama dengan yang dipakai ZXing sendiri — hijau diberi bobot lebih
      // karena mata (dan sensor kamera) paling peka di situ.
      const abu = new Uint8ClampedArray(w * h);
      for (let i = 0, j = 0; j < abu.length; i += 4, j++) {
        abu[j] = (rgba[i] + 2 * rgba[i + 1] + rgba[i + 2]) / 4;
      }

      try {
        const bitmap = new z.BinaryBitmap(new z.HybridBinarizer(
          new z.RGBLuminanceSource(abu, w, h)
        ));
        return (reader.decode(bitmap).getText() || "").trim();
      } catch {
        // NotFoundException = frame ini tidak memuat barcode. Itu keadaan
        // NORMAL berkali-kali per detik, bukan error yang perlu dilaporkan.
        return "";
      } finally {
        reader.reset();
      }
    };

    (async () => {
      // ---- 1. Nyalakan kamera ----
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } }, audio: false,
        });
        if (batal) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;

        const video = videoRef.current;
        if (video) {
          // WebKit lama hanya membaca atribut HTML-nya, bukan properti React.
          // Tanpa ini iOS memaksa video jadi layar penuh dan pemindaian mati.
          video.setAttribute("playsinline", "true");
          video.setAttribute("muted", "true");
          video.srcObject = stream;
          try {
            await video.play();
          } catch {
            // iOS kadang menolak play() walau video bisu; frame tetap mengalir
            // dan pembacaan tetap jalan, jadi jangan batalkan pemindaian.
          }
          // Frame pertama harus benar-benar ada. Membaca video 0x0 hanya
          // membuang percobaan dan membuat pemindai terasa lambat menyala.
          if (!video.videoWidth) {
            await new Promise((res) => {
              const siap = () => { video.removeEventListener("loadeddata", siap); res(); };
              video.addEventListener("loadeddata", siap);
              setTimeout(res, 3000);
            });
          }
        }
      } catch (err) {
        const pesan =
          err?.name === "NotAllowedError"
            ? "Izin kamera ditolak. Aktifkan izin kamera untuk situs ini di pengaturan browser."
            : err?.name === "NotReadableError" || err?.name === "TrackStartError"
              ? "Kamera sedang dipakai aplikasi lain. Tutup aplikasi kamera lalu coba lagi."
              : err?.name === "NotFoundError"
                ? "Tidak ada kamera yang terdeteksi di perangkat ini."
                : "Tidak bisa membuka kamera. Ketik IMEI manual saja.";
        if (!batal) { toast.error(pesan); setOpen(false); }
        return;
      }
      if (batal) return;

      // ---- 2. Pilih mesin pembaca ----
      let baca;
      if ("BarcodeDetector" in window) {
        let detector;
        try {
          detector = new window.BarcodeDetector({ formats: FORMAT_NATIF });
        } catch {
          // Sebagian format ditolak perangkat ini — pakai daftar bawaannya.
          detector = new window.BarcodeDetector();
        }
        baca = () => bacaNatif(detector);
        setStatus("Arahkan kamera ke barcode…");
      } else {
        setStatus("Menyiapkan pemindai…");
        try {
          const zx = await muatZxing();
          if (batal) return;
          baca = async () => bacaZxing(zx);
          setStatus("Arahkan kamera ke barcode…");
        } catch {
          if (!batal) {
            toast.error("Gagal memuat pemindai. Periksa koneksi, atau ketik IMEI manual.");
            setOpen(false);
          }
          return;
        }
      }

      // ---- 3. Baca berulang sampai ketemu ----
      const putar = async () => {
        if (batal || !videoRef.current) return;
        let nilai = "";
        try {
          nilai = await baca();
        } catch {
          // Frame gagal dibaca — lanjut ke frame berikutnya.
        }
        if (batal) return;
        if (nilai) {
          onDetected(nilai);
          toast.success(`Terbaca: ${nilai}`);
          close();
          return;
        }
        timerRef.current = setTimeout(putar, JEDA_PINDAI_MS);
      };
      putar();
    })();

    return () => { batal = true; stop(); };
  }, [open, onDetected, close, stop]);

  return (
    <>
      <button type="button" onClick={start} title={title} data-testid={testId}
        className="inline-flex items-center gap-1 h-9 px-2.5 rounded border border-slate-200 bg-white hover:bg-slate-50 text-xs font-medium text-slate-700 transition-colors">
        <ScanLine className="w-3.5 h-3.5" /> Scan
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4"
          data-testid="barcode-scanner-modal">
          <div className="bg-white rounded-xl overflow-hidden w-full max-w-md">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <p className="text-sm font-semibold text-slate-900 inline-flex items-center gap-2">
                <ScanLine className="w-4 h-4 text-emerald-600" /> {title}
              </p>
              <button type="button" onClick={close} data-testid="barcode-scanner-close"
                className="p-1 rounded hover:bg-slate-100 text-slate-500">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="relative bg-slate-900 aspect-[4/3]">
              <video ref={videoRef} playsInline muted autoPlay className="w-full h-full object-cover" />
              <canvas ref={canvasRef} className="hidden" />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-4/5 h-24 border-2 border-emerald-400 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]" />
              </div>
            </div>
            <div className="px-4 py-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-600">{status}</p>
              <button type="button" onClick={close}
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700">
                <CameraOff className="w-3.5 h-3.5" /> Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
